<template>
  <div
    class="bg-body-primary p-4 fixed w-full bottom-0 lg:relative lg:p-0 lg:m-0 lg:mt-24"
    :class="{
      'border-t border-linear-g-primary lg:border-none': !isProgressIndicatorVisible,
      '-ml-4': !isPaymentPage && !isSetContactPage,
    }"
  >
    <CheckoutProgressIndicator v-if="isProgressIndicatorVisible" class="mb-4" />
    <div
      v-if="isReservationInvalid"
      class="flex justify-between items-baseline flex-wrap text-alerts-warning"
    >
      <p id="retry-error">Sorry, that table is no longer available.</p>
      <ButtonDefault
        id="start-over-button"
        class="px-4 py-1.5 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
        @click="startOver"
        :label="startOverButtonText"
        guest
      />
    </div>
    <div v-else class="flex justify-start">
      <ButtonDefault
        id="back-button"
        label="Back"
        class="mr-auto font-semibold uppercase text-sm px-4 py-1.5 border border-solid border-button-guest-primary rounded font-button text-button-guest-primary"
        guest
        @click="onBack"
      />
      <ReservationCheckoutTimer class="mr-auto" />
      <ButtonDefault
        v-if="shouldRetryReservation"
        id="retry-button"
        class="px-4 py-1.5 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
        :label="retryButtonLabel"
        @click="retryReservation"
        :disabled="isRetryingReservation"
        guest
      />
      <ButtonDefault
        v-else
        id="next-button"
        class="px-4 py-1.5 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
        :class="{ invisible: hideNextButton }"
        :label="nextButtonText"
        @click="emit('click:next')"
        :disabled="isNextButtonDisabled"
        guest
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import { useFeatureFlags } from '@/common/composables/useFeatureFlags'
import { StorePerformance } from '@/models'
import CheckoutProgressIndicator from '@/modules/guestUi/components/molecules/CheckoutProgressIndicator.vue'
import ReservationCheckoutTimer from '@/modules/guestUi/components/molecules/ReservationCheckoutTimer.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { ErrorData, blankError, clearError, displayHandled } from '@/utils/errors'

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'click:next'): void
}>()

withDefaults(
  defineProps<{
    nextButtonText?: string
    isNextButtonDisabled?: boolean
    hideNextButton?: boolean
  }>(),
  {
    nextButtonText: 'Next',
    isNextButtonDisabled: false,
    hideNextButton: false,
  },
)

const checkoutStore = useCheckoutStore()
const { is_timer_expired } = storeToRefs(checkoutStore)

const { isProgressIndicatorVisible } = useFeatureFlags()

const route = useRoute()
const isSetContactPage = computed<boolean>(() => route.name === 'setContact')
const isPaymentPage = computed<boolean>(() => route.name === 'payment')

// Reservation retry logic
const isReservationInvalid = ref<boolean>(false)
const isRetryingReservation = ref<boolean>(false)
const reservationProcessError = ref<ErrorData>(blankError())
const shouldRetryReservation = computed<boolean>(() => {
  return isPaymentPage.value && is_timer_expired.value
})
async function retryReservation() {
  isRetryingReservation.value = true
  isReservationInvalid.value = false
  clearError(reservationProcessError.value)
  try {
    await checkoutStore.retryReservation()
  } catch (e) {
    isReservationInvalid.value = true
    displayHandled(e, reservationProcessError.value)
  } finally {
    isRetryingReservation.value = false
  }
}
const retryButtonLabel = computed<string>(() => {
  return isRetryingReservation.value ? 'Verifying reservation...' : 'Refresh Reservation'
})

// Start over logic
const router = useRouter()
const isStartingOver = ref<boolean>(false)
const startOverButtonText = computed<string>(() => {
  return isStartingOver.value ? 'Redirecting...' : 'Choose Another Table'
})
async function startOver() {
  isStartingOver.value = true
  const performance = cloneDeep(checkoutStore.performance) as StorePerformance
  const numberOfGuests = checkoutStore.number_of_guests
  checkoutStore.clearAll()
  try {
    await checkoutStore.setPerformance(performance, reservationProcessError.value)
    await checkoutStore.setNumberOfGuests(numberOfGuests, reservationProcessError.value)
  } catch (e) {
    await router.push({ name: 'setShow' })
  } finally {
    isReservationInvalid.value = false
    isStartingOver.value = false
  }
}

function onBack() {
  history.go(-1)
}
</script>
